import { autoinject } from 'aurelia-framework';
import { ToastService } from 'services/toast-service';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

@autoinject
export class VersionPollService {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private detected = false;
  private paused = false;

  constructor(private toastService: ToastService) {}

  public start(): void {
    if (this.intervalId !== null || this.detected) {
      return;
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    void this.checkVersion(); // Initial check immediately on start

    this.intervalId = setInterval(() => {
      if (!this.paused) {
        void this.checkVersion();
      }
    }, POLL_INTERVAL_MS);
  }

  private handleVisibilityChange = (): void => {
    this.paused = document.hidden;
  };

  private async checkVersion(): Promise<void> {
    try {
      const response = await fetch('/api/version');
      if (!response.ok) {
        return;
      }

      const data: { version: string } = await response.json();
      if (!data?.version) {
        return;
      }

      const currentVersion = localStorage.getItem('NETLOG_VERSION');

      // If the inital version is empty, set it to the current version without showing a toast, this can happen on first load or if localStorage was cleared.
      // then we assume they are on the latest version and only show a toast if we detect a change after that.
      if (currentVersion && data.version !== currentVersion) {
        this.stopPolling();
        this.toastService.showUpdateAvailable();
      }

      localStorage.setItem('NETLOG_VERSION', data.version);
    } catch {
      console.warn('Version check failed, will retry on next interval');
      // Network error — silently retry on next interval
    }
  }

  private stopPolling(): void {
    this.detected = true;
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}
